import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

const Meta = ({ label, value, testid }) => (
  <p className="break-all text-xs"><span className="text-muted-foreground">{label}</span> <span data-testid={testid} className="text-foreground">{value}</span></p>
);

export const InviteDialog = ({ entry, onClose, onDone }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState("branded");
  useEffect(() => {
    if (!entry) return;
    setPreview(null);
    api.get(`/admin/waitlist/${entry.id}/invite-preview`, { params: { style } }).then((r) => setPreview(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [entry, style]);

  const submit = async (sendEmail) => {
    setBusy(true);
    try {
      await api.post(`/admin/waitlist/${entry.id}/invite`, { send_email: sendEmail, style });
      toast.success(sendEmail ? `Invite email sent to ${entry.email}.` : "Added to Invites. No email was sent.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="invite-dialog" className="glass max-h-[92vh] w-[calc(100%-1.5rem)] max-w-3xl overflow-y-auto rounded-3xl border p-5 sm:p-7">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-normal">{preview?.already_invited ? "Resend invite" : "Invite to the founding beta"}</DialogTitle>
          <DialogDescription>Review the email before it goes out. Nothing is sent until you click Send.</DialogDescription>
        </DialogHeader>
        <label data-testid="invite-style-toggle" className="flex w-fit cursor-pointer items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-sm">
          <Switch checked={style === "personal"} onCheckedChange={(v) => setStyle(v ? "personal" : "branded")} data-testid="invite-style-switch" />
          <span>Personal style <span className="text-muted-foreground">· short plain email, logo only (better for Gmail Primary)</span></span>
        </label>
        {!preview ? <div className="h-72 rounded-2xl shimmer" /> : (
          <div className="space-y-4">
            <div className="grid gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-4 sm:grid-cols-2">
              <Meta label="To" value={preview.to} testid="invite-preview-to" />
              <Meta label="From" value={preview.from} testid="invite-preview-from" />
              <Meta label="Reply-To" value={preview.reply_to || "—"} testid="invite-preview-reply-to" />
              <Meta label="Subject" value={preview.subject} testid="invite-preview-subject" />
            </div>
            <iframe title="Invite email preview" data-testid="invite-preview-frame" srcDoc={preview.html} sandbox="allow-same-origin"
              className="h-[420px] w-full rounded-2xl border border-white/10 bg-white" />
            {preview.blockers.length > 0 && (
              <ul data-testid="invite-blockers" className="space-y-1.5 rounded-2xl border border-[#E9C98B]/35 bg-[#E9C98B]/[0.07] p-4 text-sm text-[#F3DDAE]">
                {preview.blockers.map((b) => <li key={b} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{b}</li>)}
              </ul>
            )}
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              {!preview.already_invited && (
                <Button variant="outline" disabled={busy} onClick={() => submit(false)} data-testid="invite-without-email-button" className="rounded-full">Add to Invites without email</Button>
              )}
              <Button disabled={busy || !preview.can_send} onClick={() => submit(true)} data-testid="invite-send-button" className="btn-aurora rounded-full px-6">
                <Mail className="mr-1.5 h-4 w-4" />{busy ? "Sending…" : preview.already_invited ? "Resend invite" : "Send invite"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
